import type { CustomerArchetype, CustomerWave, DayDefinition, MenuItemId } from '../game/types';

export const MENU_UNLOCK_DAYS: Record<MenuItemId, number> = {
  coffee: 1,
  cake: 1,
  tea: 3,
  lemonade: 3,
  turkishCoffee: 6,
  croissant: 7,
  toast: 8,
  cheesecake: 9,
  hotChocolate: 11,
  salep: 13,
};

export const tableCountForDay = (day: number): number => (day <= 2 ? 2 : day <= 5 ? 3 : day <= 10 ? 4 : 5);

export const menuForDay = (day: number): MenuItemId[] =>
  (Object.keys(MENU_UNLOCK_DAYS) as MenuItemId[]).filter((item) => MENU_UNLOCK_DAYS[item] <= day);

const ARCHETYPES: CustomerArchetype[] = ['STANDARD', 'PATIENT', 'GENEROUS', 'STANDARD', 'IMPATIENT'];

const makeWaves = (
  day: number,
  count: number,
  spacing: number,
  twoItemEvery = 0,
): CustomerWave[] => {
  const menu = menuForDay(day);
  return Array.from({ length: count }, (_, index) => {
    const primary = menu[(index * 3 + day) % menu.length];
    const secondary = menu[(index * 3 + day + 2) % menu.length];
    const isPair = twoItemEvery > 0 && index > 1 && (index + day) % twoItemEvery === 0 && primary !== secondary;
    return {
      at: 1.5 + index * spacing,
      archetype: ARCHETYPES[(index + day) % ARCHETYPES.length],
      order: isPair ? [primary, secondary] : primary,
    };
  });
};

export const DAYS: DayDefinition[] = [
  {
    id: 1, title: 'İlk Servis', goal: 70, challenge: 'Hiç müşteri kaybetme', tables: 2,
    menu: ['coffee', 'cake'], decorStage: 1,
    waves: [
      { at: 1.5, archetype: 'PATIENT', order: 'coffee' }, { at: 12, archetype: 'STANDARD', order: 'cake' },
      { at: 25, archetype: 'GENEROUS', order: 'coffee' }, { at: 39, archetype: 'STANDARD', order: 'coffee' },
      { at: 52, archetype: 'PATIENT', order: 'cake' },
    ],
  },
  {
    id: 2, title: 'Öğle Telaşı', goal: 90, challenge: 'Tüm masaları servis sonunda temiz bırak', tables: 2,
    menu: ['coffee', 'cake'], decorStage: 1,
    waves: [
      { at: 1.5, archetype: 'STANDARD', order: 'coffee' }, { at: 8.5, archetype: 'PATIENT', order: 'cake' },
      { at: 15, archetype: 'STANDARD', order: 'coffee' }, { at: 23, archetype: 'GENEROUS', order: 'cake' },
      { at: 31, archetype: 'STANDARD', order: 'coffee' }, { at: 40, archetype: 'PATIENT', order: 'cake' },
    ],
  },
  {
    id: 3, title: 'Yeni Tatlar', goal: 112, challenge: 'Görkem’in sürprizini kaçırma', tables: 3,
    menu: ['coffee', 'cake', 'tea', 'lemonade'], decorStage: 2,
    waves: [
      { at: 1.5, archetype: 'PATIENT', order: 'tea' }, { at: 7, archetype: 'STANDARD', order: 'lemonade' },
      { at: 13, archetype: 'IMPATIENT', order: 'coffee' }, { at: 20, archetype: 'GENEROUS', order: 'cake' },
      { at: 27, archetype: 'STANDARD', order: 'tea' }, { at: 35, archetype: 'IMPATIENT', order: 'lemonade' },
      { at: 43, archetype: 'PATIENT', order: 'coffee' },
    ],
    specialEvents: [{ at: 30, type: 'GORKEM_VISIT', order: 'lemonade', bonus: 18 }],
  },
  {
    id: 4, title: 'Birlikte Daha Hızlı', goal: 140, challenge: 'Fevzi en az iki kez yardım etsin', tables: 3,
    menu: ['coffee', 'cake', 'tea', 'lemonade'], fevziHelper: true, decorStage: 2,
    waves: [
      { at: 1.5, archetype: 'STANDARD', order: 'coffee' }, { at: 6.5, archetype: 'PATIENT', order: 'tea' },
      { at: 12, archetype: 'GENEROUS', order: 'cake' }, { at: 18, archetype: 'IMPATIENT', order: 'lemonade' },
      { at: 25, archetype: 'STANDARD', order: 'coffee' }, { at: 32, archetype: 'PATIENT', order: 'cake' },
      { at: 39, archetype: 'IMPATIENT', order: 'tea' }, { at: 47, archetype: 'GENEROUS', order: 'lemonade' },
    ],
  },
  {
    id: 5, title: 'Köşenin Büyük Günü', goal: 165, challenge: 'En az 3 kombo yap', tables: 3,
    menu: ['coffee', 'cake', 'tea', 'lemonade'], fevziHelper: true, decorStage: 3,
    waves: [
      { at: 1.5, archetype: 'PATIENT', order: 'coffee' }, { at: 6, archetype: 'STANDARD', order: 'tea' },
      { at: 11, archetype: 'IMPATIENT', order: 'lemonade' }, { at: 17, archetype: 'GENEROUS', order: 'cake' },
      { at: 23, archetype: 'STANDARD', order: 'coffee' }, { at: 29, archetype: 'PATIENT', order: 'lemonade' },
      { at: 35, archetype: 'IMPATIENT', order: 'tea' }, { at: 42, archetype: 'GENEROUS', order: 'cake' },
      { at: 49, archetype: 'STANDARD', order: 'coffee' },
    ],
    specialEvents: [{ at: 59, type: 'GORKEM_VISIT', order: 'tea', bonus: 24 }],
  },
  {
    id: 6, title: 'Dördüncü Masa', goal: 180, challenge: 'En az 7 müşteriyi mutlu uğurla', tables: 4,
    menu: menuForDay(6), fevziHelper: true, decorStage: 3, bonusObjective: { kind: 'SERVE', target: 7, reward: 18 }, waves: makeWaves(6, 9, 7.5),
  },
  {
    id: 7, title: 'Fırından Yeni', goal: 205, challenge: 'Hiç müşteri kaybetme', tables: 4,
    menu: menuForDay(7), fevziHelper: true, decorStage: 3, bonusObjective: { kind: 'NO_LOST', target: 0, reward: 20 }, waves: makeWaves(7, 10, 7.3),
  },
  {
    id: 8, title: 'Tost Saati', goal: 215, challenge: 'En az 3 kombo yap', tables: 4,
    menu: menuForDay(8), fevziHelper: true, decorStage: 3, bonusObjective: { kind: 'COMBO', target: 3, reward: 20 }, waves: makeWaves(8, 10, 7.1),
  },
  {
    id: 9, title: 'Tatlı Vitrini', goal: 225, challenge: 'Görkem’in hediyesini kazan', tables: 4,
    menu: menuForDay(9), fevziHelper: true, decorStage: 3, bonusObjective: { kind: 'SERVE', target: 8, reward: 22 }, waves: makeWaves(9, 10, 7),
    specialEvents: [{ at: 52, type: 'GORKEM_VISIT', order: 'cheesecake', bonus: 30 }],
  },
  {
    id: 10, title: 'Akşam Kalabalığı', goal: 245, challenge: 'Servisi kirli masa bırakmadan bitir', tables: 4,
    menu: menuForDay(10), fevziHelper: true, decorStage: 4, bonusObjective: { kind: 'CLEAN_END', target: 0, reward: 22 }, waves: makeWaves(10, 11, 6.8, 7),
  },
  {
    id: 11, title: 'Beş Masa', goal: 260, challenge: 'En az 8 müşteriye servis yap', tables: 5,
    menu: menuForDay(11), fevziHelper: true, decorStage: 4, bonusObjective: { kind: 'SERVE', target: 8, reward: 24 }, waves: makeWaves(11, 11, 6.7, 6),
  },
  {
    id: 12, title: 'Tam Kapasite', goal: 280, challenge: 'En az 4 kombo yap', tables: 5,
    menu: menuForDay(12), fevziHelper: true, decorStage: 4, bonusObjective: { kind: 'COMBO', target: 4, reward: 24 }, waves: makeWaves(12, 12, 6.6, 6),
  },
  {
    id: 13, title: 'Kış Sıcağı', goal: 320, challenge: 'En az 5 sıcak içecek servis et', tables: 5,
    menu: menuForDay(13), fevziHelper: true, decorStage: 4, bonusObjective: { kind: 'HOT_DRINKS', target: 5, reward: 26 }, waves: makeWaves(13, 12, 6.5, 5),
  },
  {
    id: 14, title: 'Uyumlu Telaş', goal: 330, challenge: 'Hiç müşteri kaybetme', tables: 5,
    menu: menuForDay(14), fevziHelper: true, decorStage: 4, bonusObjective: { kind: 'NO_LOST', target: 0, reward: 28 }, waves: makeWaves(14, 12, 6.4, 5),
  },
  {
    id: 15, title: 'Altın Öğleden Sonra', goal: 345, challenge: 'En az 5 kombo yap', tables: 5,
    menu: menuForDay(15), fevziHelper: true, decorStage: 5, bonusObjective: { kind: 'COMBO', target: 5, reward: 30 }, waves: makeWaves(15, 13, 6.3, 5),
    specialEvents: [{ at: 60, type: 'GORKEM_VISIT', order: 'hotChocolate', bonus: 36 }],
  },
  {
    id: 16, title: 'Mahallenin Gözdesi', goal: 350, challenge: 'En az 10 müşteriyi mutlu uğurla', tables: 5,
    menu: menuForDay(16), fevziHelper: true, decorStage: 5, bonusObjective: { kind: 'SERVE', target: 10, reward: 30 }, waves: makeWaves(16, 13, 6.2, 5),
  },
  {
    id: 17, title: 'Hızlı Eller', goal: 385, challenge: 'En az 5 kombo yap', tables: 5,
    menu: menuForDay(17), fevziHelper: true, decorStage: 5, bonusObjective: { kind: 'COMBO', target: 5, reward: 32 }, waves: makeWaves(17, 14, 6.1, 4),
  },
  {
    id: 18, title: 'Dolu Dolu', goal: 405, challenge: 'Hiç müşteri kaybetme', tables: 5,
    menu: menuForDay(18), fevziHelper: true, decorStage: 5, bonusObjective: { kind: 'NO_LOST', target: 0, reward: 34 }, waves: makeWaves(18, 14, 6, 4),
  },
  {
    id: 19, title: 'Büyük Gün Öncesi', goal: 420, challenge: 'En az ₺45 bahşiş kazan', tables: 5,
    menu: menuForDay(19), fevziHelper: true, decorStage: 5, bonusObjective: { kind: 'TIPS', target: 45, reward: 36 }, waves: makeWaves(19, 15, 5.9, 4),
  },
  {
    id: 20, title: 'Bizim Köşe', goal: 385, challenge: 'Final servisini birlikte tamamla', tables: 5,
    menu: menuForDay(20), fevziHelper: true, decorStage: 5, bonusObjective: { kind: 'SERVE', target: 11, reward: 40 }, waves: makeWaves(20, 13, 6.1, 4), finale: true,
  },
];

export const getDay = (id: number): DayDefinition => DAYS.find((day) => day.id === id) ?? DAYS[0];
